/**
 * <cc-product-extras> — product page rows under the main product:
 *   related        Shopify recommendations (intent=related); replaces the server-rendered same-collection fallback when results come back.
 *   complementary  Shopify recommendations (intent=complementary, set in Search & Discovery); stays hidden when empty.
 *   recent         last 4 products viewed in this browser (localStorage), excluding the current one; hidden when empty.
 *
 * Uses the documented Ajax endpoint `{routes.product_recommendations_url}.json`, so no section rendering is required.
 * Cards are cloned from the <template data-extras-card> authored in Liquid.
 */
const STORAGE_KEY = 'cc-recently-viewed';
const MAX_RECENT = 4; // spec: the last 4 products

class CcProductExtras extends HTMLElement {
  connectedCallback() {
    this.template = this.querySelector('template[data-extras-card]');
    this.money = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'fr', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
    });
    this.#recent();
    this.#recommendations('related', 4);
    this.#recommendations('complementary', 4);
  }

  async #recommendations(intent, limit) {
    const section = this.querySelector(`[data-extras="${intent}"]`);
    if (!section || !this.dataset.recommendationsUrl) return;
    const url = new URL(`${this.dataset.recommendationsUrl}.json`, location.origin);
    url.searchParams.set('product_id', this.dataset.productId);
    url.searchParams.set('limit', String(limit));
    url.searchParams.set('intent', intent);
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const data = await response.json();
      const products = (data.products || []).filter((p) => p.available !== false).slice(0, limit);
      if (!products.length) return;
      this.#fill(
        section,
        products.map((p) => ({
          url: p.url,
          title: p.title,
          image: p.featured_image ? this.#sized(p.featured_image, 600) : '',
          price: this.money.format(p.price / 100),
        }))
      );
    } catch {
      /* keep the fallback (related) or the hidden state (complementary) */
    }
  }

  #recent() {
    let list = [];
    try {
      list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {}
    let current = null;
    try {
      current = JSON.parse(this.dataset.current || 'null');
    } catch {}
    const others = list.filter((item) => item && item.handle !== current?.handle).slice(0, MAX_RECENT);
    const section = this.querySelector('[data-extras="recent"]');
    if (section && others.length) this.#fill(section, others);
    if (current) {
      const next = [current, ...list.filter((item) => item && item.handle !== current.handle)].slice(0, MAX_RECENT + 1);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {}
    }
  }

  #fill(section, items) {
    const list = section.querySelector('[data-extras-list]');
    if (!list || !this.template) return;
    const fragment = document.createDocumentFragment();
    for (const item of items) {
      const node = this.template.content.cloneNode(true);
      const link = node.querySelector('[data-card-link]');
      link.href = item.url;
      node.querySelector('[data-card-title]').textContent = item.title;
      node.querySelector('[data-card-price]').textContent = item.price;
      const img = node.querySelector('[data-card-image]');
      if (item.image) {
        img.src = item.image;
        img.alt = item.title;
      } else {
        img.remove();
      }
      fragment.append(node);
    }
    list.replaceChildren(fragment);
    section.hidden = false;
  }

  #sized(src, width) {
    try {
      const url = new URL(src.startsWith('//') ? `https:${src}` : src, location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return src;
    }
  }
}

if (!customElements.get('cc-product-extras')) customElements.define('cc-product-extras', CcProductExtras);
